/**
 * BackupActivityDrawer — the universal live-progress panel (Spec 201). Lists every
 * backup/restore the user launched (from any tab / entity surface) with live status,
 * so progress is never trapped in the tab where it was kicked off. Opened from the
 * Backup section header; backed by the persisted backup-activity store.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React from 'react';
import { Trash2 } from 'lucide-react';
import { Drawer, Button, EmptyState } from '@/components/ui';
import { useBackupActivity } from '../store/backupActivityStore';
import { LiveExecutionStatus } from './restore/LiveExecutionStatus';

export const BackupActivityDrawer: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const entries = useBackupActivity((s) => s.entries);
  const clear = useBackupActivity((s) => s.clear);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Backup activity"
      width="lg"
      footer={
        entries.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={clear}>
            <Trash2 className="h-4 w-4" /> Clear history
          </Button>
        ) : undefined
      }
    >
      {entries.length === 0 ? (
        <EmptyState
          title="No recent activity"
          message="Backups and restores you launch — from any tab — appear here with live status, and persist across a page refresh."
        />
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <LiveExecutionStatus
              key={e.executionName}
              executionName={e.executionName}
              verb={e.verb}
              subject={e.subject}
              startedAt={e.startedAt}
            />
          ))}
        </div>
      )}
    </Drawer>
  );
};

export default BackupActivityDrawer;
